import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { Toaster } from '@/components/ui/toaster'
import { queryClient } from '@/lib/queryClient'
import { AuthProvider } from '@/contexts/AuthContext'
import { getDomainInfo } from '@/lib/domain'
import { initSentry } from '@/lib/sentry'
import { BuilderApp } from './App'
import './index.css'

// Initialize Sentry before rendering
initSentry()

/**
 * Get the viewer URL based on environment
 */
function getViewerUrl(subdomain: string): string {
  // In development, redirect to the Next.js viewer on port 3001
  if (import.meta.env.DEV) {
    return `http://${subdomain}.localhost:3001/${subdomain}`
  }
  // In production, use the viewer domain from environment
  const viewerDomain = import.meta.env.VITE_VIEWER_DOMAIN || 'bookedridespages.com'
  return `https://${subdomain}.${viewerDomain}`
}

/**
 * Check if we're already on the viewer app (port 3001 with subdomain)
 */
function isAlreadyOnViewer(): boolean {
  const hostname = window.location.hostname
  const port = window.location.port
  return hostname.endsWith('.localhost') && port === '3001'
}

/**
 * Root Application
 * Routes between Builder (admin) and Viewer (public sites) based on domain
 */
function App() {
  const { type, subdomain } = getDomainInfo()
  const [shouldRedirect, setShouldRedirect] = useState(false)

  // Check if we should redirect to viewer
  const needsRedirect = (type === 'viewer' || type === 'custom') && subdomain && !isAlreadyOnViewer()

  // Perform redirect in useEffect to satisfy React Compiler purity rules
  useEffect(() => {
    if (needsRedirect && subdomain) {
      setShouldRedirect(true)
      window.location.href = getViewerUrl(subdomain)
    }
  }, [needsRedirect, subdomain])

  // Show nothing while redirecting
  if (needsRedirect || shouldRedirect) {
    return null
  }

  // Builder/Admin application
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="flex h-screen flex-col items-center justify-center gap-4">
          <h1 className="text-2xl font-bold">Something went wrong</h1>
          <p className="text-muted-foreground">An unexpected error occurred. Please refresh the page.</p>
          <button
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
        </div>
      }
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <BuilderApp />
            <Toaster />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
)
